import { GQL_EmployeeCreate } from '@/queries/global';
import Decimal from '@/utils/decimal';
import dayjs from '@/utils/dayjs';
import sortBy from 'lodash/sortBy';

export const employeeCreateObject: (() => GQL_EmployeeCreate) = () => ({
  unitId: '',

  firstName: '',
  lastName: '',
  birthDate: '',

  phoneCountryCode: '',
  phone: '',
  user: {
    login: '',
    cellphone: '',
    cellphoneCountryCode: '',
  },

  address: '',
  city: '',
  zip: '',
  countryCode: '',

  postboxNumber: '',
  postboxPlace: '',
  postboxZip: '',
  postboxCity: '',

  bban: '',
  iban: '',
  swift: '',
  socialSecurityNumber: '',
  temporarySocialSecurityNumber: '',
  vacationMoneyPercentage: new Decimal(0),

  creditDays: 0,
});

export type EmployeeSearchMatchedData = {
  firstName: string;
  lastName: string;
  user: {
    login: string;
    cellphone: string;
    cellphoneCountryCode: string;
  };
  phoneCountryCode: string;
  countryCode: string;
  socialSecurityNumber: string;
  temporarySocialSecurityNumber: string;
  address: string;
  zip: string;
  city: string;
  phone: string;
  birthDate: string;
};

// Individual digits are allocated as follows:
// born 1854 - 1899: allocated from series 500 - 749
// born 1900 - 1999: allocated from series 000 - 499
// born 1940 - 1999: also allocated from series 900 - 999
// born 2000 - 2039: allocated from series 500 - 999
function getYearRange(series: number, year: number): string {
  if (series >= 500 && series <= 749 && year >= 54 && year <= 99) {
    return '18';
  } else if (
    (series >= 0 && series <= 499) ||
    (series >= 900 && series <= 999 && year >= 40 && year <= 99)
  ) {
    return '19';
  } else if (series >= 500 && series <= 999 && year >= 0 && year <= 39) {
    return '20';
  }

  return '20';
}

// SSN for Norway
// https://www.skatteetaten.no/en/person/National-Registry/Birth-and-name-selection/Children-born-in-Norway/National-ID-number/

// Structure of the national identity number
// The national identity number consists of 11 digits
// The first six digits represent the date of birth in the order date, month, year
// The next three digits are an individual number, the third digit of which indicates gender – even numbers for women and odd numbers for men
// The last two digits are control digits
// The last five digits of the national identity number constitute the personal number
// The three digits comprising the individual number are allocated sequentially within the specific date of birth.
export function norwegianSsnToBirthDate(ssn: string, tssn: string): string {
  let number = ssn;
  if (ssn.length !== 11) {
    if (tssn.length !== 11) return '';
    number = (
      Number(tssn.substring(0, 2)) - 40
    ).toString()
      .padStart(2, '0') +
      tssn.substring(2);
  }

  const day = number.substr(0, 2);
  let month = number.substr(2, 2);
  const year = number.substr(4, 2);
  const series = Number(number.substr(6, 3));

  // In the norwegian tax administrations new test system, Tenor, the month in all ssns
  // have 80 added to it. So you should be able to see that it is fictional.
  if (Number(month) > 80) {
    month = (Number(month) - 80).toString().padStart(2, '0');
  }

  // const controlDigits = number.substr(9, 2);
  const parsedYear = getYearRange(series, Number(year)) + year;
  return `${parsedYear}-${month}-${day}`;
}

// In the norwegian tax administrations new test system, Tenor, the month in all ssns
// have 80 added to it. So you should be able to see that it is fictional.
export function birthDateMatchesSsn(
  birthDate: string | null,
  socialSecurityNumber: string | null,
  temporarySocialSecurityNumber: string | null,
): boolean {
  if (!birthDate) return true;

  const date = dayjs.strict(birthDate);
  const year = date.format('YY');
  const month = date.format('MM');
  const day = date.format('DD');

  if (socialSecurityNumber) {
    return (
      socialSecurityNumber.startsWith(`${day}${month}${year}`) ||
      socialSecurityNumber.startsWith(`${day}${Number(month) + 80}${year}`)
    );
  } else if (temporarySocialSecurityNumber) {
    return (
      temporarySocialSecurityNumber.startsWith(`${Number(day) + 40}${month}${year}`) ||
      temporarySocialSecurityNumber.startsWith(`${Number(day) + 40}${Number(month) + 80}${year}`)
    );
  }

  return true;
}

import { GQL_employeeFormUnitSettings_currentUnit_unitSetting_unitPaycheckSetting } from '@/queries/__generated__/employeeFormUnitSettings';
import { GQL_WorkRelationEmployeeSave } from '@/queries/global';
export function buildNewWorkRelation(unitPaycheckSetting: GQL_employeeFormUnitSettings_currentUnit_unitSetting_unitPaycheckSetting): GQL_WorkRelationEmployeeSave {
  return {
    id: null,
    subUnitId: unitPaycheckSetting.defaultWorkRelation.subUnitId,
    occupationId: unitPaycheckSetting.defaultWorkRelation.occupationId,
    startDate: null,
    endDate: null,
    endReason: null,
    durationType: unitPaycheckSetting.defaultWorkRelation.durationType,
    samePositionSince: null,
    shiftType: unitPaycheckSetting.defaultWorkRelation.shiftType,
    employmentType: unitPaycheckSetting.defaultWorkRelation.employmentType,
    employmentPercentage: unitPaycheckSetting.defaultWorkRelation.employmentPercentage,
    employmentPercentageUpdated: null,
    hoursEveryWeekWithFullEmployment: unitPaycheckSetting.defaultWorkRelation.hoursEveryWeekWithFullEmployment,
    lastSalaryChange: null,
    leaveAndLayoffs: [],
  };
}

// Oldest first; pensions (no start date) last.
export function sortWorkRelations<T extends { id: string | null; startDate: string | null }>(
  workRelations: T[],
): T[] {
  return sortBy(workRelations, [
    workRelation => workRelation.startDate || '9999-12-31',
    workRelation => Number(workRelation.id),
  ]);
}

import { GQL_EmployeeTaxCardEmployeeSave } from '@/queries/global';
export function buildNewEmployeeTaxCard(): GQL_EmployeeTaxCardEmployeeSave {
  return {
    id: null,
    fetchedAt: '',
    taxTable: '',
    taxPercentage: null,
    taxExemption: false,
    file: null,
    taxDeductionMethod: null,
  };
}

// The employee page shows this year's and last year's tax cards and folds
// away cards for earlier calendar years.
export function isOlderTaxCard(taxCard: { calendarYear?: number | null }, currentYear: number): boolean {
  return taxCard.calendarYear != null && taxCard.calendarYear < currentYear - 1;
}

import { GQL_EmployeeUpdate } from '@/queries/global';
export type GQL_EmployeeForm = GQL_EmployeeUpdate & {
  user: {
    login: string;
    cellphone: string;
    cellphoneCountryCode: string;
  };
};

export function postEmployeeCreateHooks(
  employee: GQL_EmployeeForm,
  unitPaycheckSetting: GQL_employeeFormUnitSettings_currentUnit_unitSetting_unitPaycheckSetting,
): void {
  employee.workRelations!.push(buildNewWorkRelation(unitPaycheckSetting));
  employee.employeeTaxCards!.push(buildNewEmployeeTaxCard());
}

export const taxDeductionMethods = [
  "MAIN_EMPLOYER_TABLE",
  "MAIN_EMPLOYER_PERCENTAGE",
  "SECONDARY_EMPLOYER_PERCENTAGE",
  "TAX_EXEMPTION",
];
